import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { CheckCircle2, Edit, Loader2, AlertTriangle } from 'lucide-react';
import type { WizardStage } from '../OneOnOneWizard';
import type { PopulatedInteraction } from '@momentum/types';
import {
  InteractionType,
  FeedbackStatus,
  OneOnOneStatus,
} from '@momentum/types';
import { GoalsSummarySwiper } from './components/GoalsSummarySwiper';
import { MeetingNotes } from './components/MeetingNotes';
import { useUserAssignedGoals } from '@/shared/hooks/useGoals';
import {
  useGetOneOnOneById,
  useUpdateOneOnOne,
} from '@/shared/hooks/useOneOnOne';
import { UserSelectorAvatar } from '@/shared/components/user-selector/UserSelectorAvatar';
import { useEmployee } from '@/shared/hooks/useEmployees';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';

interface SummaryStageProps {
  sessionId: string;
  employeeId: string;
  onNavigate: (stage: WizardStage) => void;
  onSubmit: (meetingNotes: string[]) => Promise<void>;
}

export const SummaryStage = ({
  sessionId,
  employeeId,
  onNavigate,
  onSubmit,
}: SummaryStageProps) => {
  const { t } = useTranslation('oneOnOne');
  const currentEmployee = useEmployeeContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState<string[]>([]);
  const { mutate: updateOneOnOne } = useUpdateOneOnOne();

  // Fetch one-on-one session data
  const { data: oneOnOneData, isLoading: isLoadingOneOnOne } =
    useGetOneOnOneById(sessionId);

  // Fetch goals and interactions data for the specified employee
  const { data: goalsWithInteractions, isLoading: isLoadingGoals } =
    useUserAssignedGoals(
      employeeId,
      oneOnOneData?.meetingType,
      oneOnOneData?.managerId
    );

  // Fetch employee data for the header
  const { data: employee } = useEmployee(employeeId, currentEmployee.companyId);

  // Initialize meeting notes from existing one-on-one data
  useEffect(() => {
    if (oneOnOneData?.notes?.manager) {
      setMeetingNotes(oneOnOneData.notes.manager);
    }
  }, [oneOnOneData]);

  const isLoading = isLoadingOneOnOne || isLoadingGoals;

  // Check if current user is employee or manager
  const isEmployee =
    oneOnOneData?.employeeId.toString() === currentEmployee._id.toString();
  const isManager =
    oneOnOneData?.managerId.toString() === currentEmployee._id.toString();
  const isReadyForSubmission =
    oneOnOneData?.status === OneOnOneStatus.READY_FOR_SUBMISSION;
  const isCompleted = oneOnOneData?.status === OneOnOneStatus.COMPLETED;

  // Transform API response to separate goals and interactions by goal
  const { goals, interactionsByGoal } = useMemo(() => {
    if (!goalsWithInteractions) {
      return { goals: [], interactionsByGoal: {} };
    }

    const goalsData = goalsWithInteractions.map((item) => {
      const { interactions, ...goal } = item;
      return goal;
    });

    const interactionsMap = goalsWithInteractions.reduce((acc, item) => {
      acc[item._id] = item.interactions as PopulatedInteraction[];
      return acc;
    }, {} as Record<string, PopulatedInteraction[]>);

    return { goals: goalsData, interactionsByGoal: interactionsMap };
  }, [goalsWithInteractions]);

  // Calculate blockages count
  const totalBlockagesCount = useMemo(() => {
    return Object.values(interactionsByGoal).reduce((total, interactions) => {
      const blockagesInGoal = interactions.filter((interaction) => {
        return (
          interaction.type === InteractionType.FEEDBACK &&
          'status' in interaction &&
          interaction.status !== FeedbackStatus.FINISHED
        );
      }).length;
      return total + blockagesInGoal;
    }, 0);
  }, [interactionsByGoal]);

  // Calculate total interactions count
  const totalInteractionsCount = useMemo(() => {
    return Object.values(interactionsByGoal).reduce(
      (total, interactions) => total + interactions.length,
      0
    );
  }, [interactionsByGoal]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit(meetingNotes);
    } catch (error) {
      console.error('Failed to submit one-on-one:', error);
      setIsSubmitting(false);
    }
  };

  const handleNotesChange = (updatedNotes: string[]) => {
    setMeetingNotes(updatedNotes);
    if (isEmployee) {
      updateOneOnOne(
        { id: sessionId, data: { notes: { manager: updatedNotes } } },
        {
          onError: () => {
            toast.error(t('failedToSaveNotes'));
          },
        }
      );
    }
  };

  if (isLoading) {
    return (
      <Card className="border-none bg-white/90 shadow-lg backdrop-blur">
        <CardContent className="flex items-center justify-center gap-3 py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-primary" />
          <p className="text-muted-foreground">Loading goals...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-md">
        <div className="flex items-center gap-4">
          {employee && (
            <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
              <UserSelectorAvatar
                fullName={`${employee.personalInfo.firstName} ${employee.personalInfo.lastName}`}
                imageUrl={employee.personalInfo.imageUrl}
                size="lg"
                shape="square"
              />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              <h2 className="text-xl font-bold text-slate-900">
                {t('oneOnOneSummary')}
              </h2>
            </div>
            {employee && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                {employee.personalInfo.firstName}{' '}
                {employee.personalInfo.lastName}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card className="border-slate-200 bg-white shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-emerald-600/10 p-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t('totalInteractions')}
                </p>
                <p className="mt-1 text-3xl font-bold text-emerald-600">
                  {totalInteractionsCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`border shadow-md ${
            totalBlockagesCount > 0
              ? 'border-amber-200 bg-amber-50'
              : 'border-slate-200 bg-white'
          }`}
        >
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div
                className={`rounded-lg p-3 ${
                  totalBlockagesCount > 0 ? 'bg-amber-100' : 'bg-slate-100'
                }`}
              >
                <AlertTriangle
                  className={`h-6 w-6 ${
                    totalBlockagesCount > 0
                      ? 'text-amber-600'
                      : 'text-slate-400'
                  }`}
                />
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${
                    totalBlockagesCount > 0
                      ? 'text-amber-700'
                      : 'text-muted-foreground'
                  }`}
                >
                  {t('openBlockages')}
                </p>
                <p
                  className={`mt-1 text-3xl font-bold ${
                    totalBlockagesCount > 0
                      ? 'text-amber-600'
                      : 'text-slate-400'
                  }`}
                >
                  {totalBlockagesCount}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goals and Meeting Notes Section */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
        {/* Goals Card */}
        <Card className="w-full h-full border-none bg-white/90 shadow-lg backdrop-blur lg:w-1/2 flex flex-col">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl text-slate-900">
                  {t('goalsReview')}
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('reviewYourGoalsAndInteractions')}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onNavigate('goals-review')}
                className="gap-2"
              >
                <Edit className="h-4 w-4" />
                {t('editGoalsReview')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <GoalsSummarySwiper goals={goals} />
          </CardContent>
        </Card>

        {/* Meeting Notes Card */}
        <div className="w-full lg:w-1/2">
          <MeetingNotes
            notes={meetingNotes}
            onNotesChange={handleNotesChange}
            className="h-full"
            readOnly={!isReadyForSubmission}
          />
        </div>
      </div>

      {/* Submit Section */}
      {isCompleted ? (
        <Card className="border-none bg-gradient-to-br from-slate-50 to-slate-100 shadow-lg backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('oneOnOneCompleted')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('oneOnOneCompletedDescription')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : isEmployee && isReadyForSubmission ? (
        <Card className="border-none bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-8">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('sentToManager')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('waitingForManagerToSubmit')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : isManager && isReadyForSubmission ? (
        <Card className="border-none bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg backdrop-blur">
          <CardContent className="flex flex-col items-center justify-center gap-4 py-8 md:flex-row md:justify-between">
            <div className="text-center md:text-start">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('readyToSubmit')}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('submitDescription')}
              </p>
            </div>
            <Button
              size="lg"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="min-w-[200px] bg-emerald-600 hover:bg-emerald-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submitOneOnOne')
              )}
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
